import JSZip, { type JSZipObject } from 'jszip'

import { openBlobArchive } from '@/lib/ooxml/blobArchive'
import type { OoxmlMediaResolver, OoxmlRelationship } from '@/lib/ooxml/types'
import {
  attributeByLocalName,
  descendantsByName,
  parseOoxmlXml,
} from '@/lib/ooxml/xml'

const MAX_SOURCE_BYTES = 256 * 1024 * 1024
const MAX_ARCHIVE_ENTRIES = 10_000
const MAX_ARCHIVE_ENTRY_BYTES = 128 * 1024 * 1024
const MAX_ARCHIVE_EXPANDED_BYTES = 512 * 1024 * 1024
const MAX_COMPRESSION_RATIO = 1_000
const MAX_XML_CHARACTERS_PER_PART = 16 * 1024 * 1024
const MAX_XML_CHARACTERS_TOTAL = 64 * 1024 * 1024
const GENERIC_BINARY_CONTENT_TYPE = 'application/octet-stream'

interface TrustedImageFormat {
  contentType: `image/${string}`
  matchesSignature: (bytes: Uint8Array) => boolean
}

const bytesMatch = (
  bytes: Uint8Array,
  expected: readonly number[],
  offset = 0,
) =>
  bytes.length >= offset + expected.length &&
  expected.every((value, index) => bytes[offset + index] === value)

const pngFormat: TrustedImageFormat = {
  contentType: 'image/png',
  matchesSignature: (bytes) =>
    bytesMatch(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
}
const jpegFormat: TrustedImageFormat = {
  contentType: 'image/jpeg',
  matchesSignature: (bytes) => bytesMatch(bytes, [0xff, 0xd8, 0xff]),
}
const gifFormat: TrustedImageFormat = {
  contentType: 'image/gif',
  matchesSignature: (bytes) =>
    bytesMatch(bytes, [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
    bytesMatch(bytes, [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]),
}
const bitmapFormat: TrustedImageFormat = {
  contentType: 'image/bmp',
  matchesSignature: (bytes) => {
    if (bytes.length < 54 || !bytesMatch(bytes, [0x42, 0x4d])) {
      return false
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const pixelOffset = view.getUint32(10, true)
    const dibHeaderSize = view.getUint32(14, true)
    return (
      dibHeaderSize >= 40 &&
      pixelOffset >= 14 + dibHeaderSize &&
      pixelOffset < bytes.length &&
      view.getInt32(18, true) > 0 &&
      view.getInt32(22, true) !== 0
    )
  },
}
const webpFormat: TrustedImageFormat = {
  contentType: 'image/webp',
  matchesSignature: (bytes) =>
    bytes.length >= 16 &&
    bytesMatch(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    bytesMatch(bytes, [0x57, 0x45, 0x42, 0x50], 8) &&
    (bytesMatch(bytes, [0x56, 0x50, 0x38, 0x20], 12) ||
      bytesMatch(bytes, [0x56, 0x50, 0x38, 0x4c], 12) ||
      bytesMatch(bytes, [0x56, 0x50, 0x38, 0x58], 12)),
}
const TRUSTED_IMAGE_FORMAT_BY_EXTENSION = new Map<string, TrustedImageFormat>([
  ['bmp', bitmapFormat],
  ['gif', gifFormat],
  ['jpe', jpegFormat],
  ['jpeg', jpegFormat],
  ['jpg', jpegFormat],
  ['png', pngFormat],
  ['webp', webpFormat],
])

interface InspectableZipObject extends JSZipObject {
  _data?: {
    compressedSize?: number
    uncompressedSize?: number
  }
}

interface ContentTypes {
  defaults: Map<string, string>
  overrides: Map<string, string>
}

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError')
  }
}

const decodePathSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

export const normalizeOpcPartName = (path: string) => {
  const normalizedSegments: string[] = []
  for (const segment of path.replaceAll('\\', '/').split('/')) {
    if (!segment || segment === '.') {
      continue
    }
    const decodedSegment = decodePathSegment(segment)
    if (
      decodedSegment === '..' ||
      decodedSegment.includes('/') ||
      decodedSegment.includes('\\') ||
      decodedSegment.includes('\0')
    ) {
      throw new Error('The Office document contains an unsafe package path.')
    }
    normalizedSegments.push(decodedSegment)
  }
  return normalizedSegments.join('/')
}

export const getOpcRelationshipPartName = (sourcePart: string) => {
  const slashIndex = sourcePart.lastIndexOf('/')
  const directory = slashIndex >= 0 ? sourcePart.slice(0, slashIndex + 1) : ''
  const fileName =
    slashIndex >= 0 ? sourcePart.slice(slashIndex + 1) : sourcePart
  return `${directory}_rels/${fileName}.rels`
}

export const resolveOpcRelationshipTarget = (
  sourcePart: string,
  target: string,
) => {
  const targetWithoutFragment = target.split('#', 1)[0] ?? ''
  if (!targetWithoutFragment) {
    throw new Error(
      'The Office document contains an empty relationship target.',
    )
  }
  if (/^[a-z][a-z\d+.-]*:/i.test(targetWithoutFragment)) {
    throw new Error('External relationship targets are not package parts.')
  }

  const resolvedSegments = targetWithoutFragment.startsWith('/')
    ? []
    : sourcePart.split('/').slice(0, -1)
  for (const rawSegment of targetWithoutFragment
    .replaceAll('\\', '/')
    .split('/')) {
    const segment = decodePathSegment(rawSegment)
    if (!segment || segment === '.') {
      continue
    }
    if (segment === '..') {
      if (resolvedSegments.length === 0) {
        throw new Error('The Office relationship escapes the package.')
      }
      resolvedSegments.pop()
      continue
    }
    if (segment.includes('\0')) {
      throw new Error(
        'The Office document contains an unsafe relationship path.',
      )
    }
    resolvedSegments.push(segment)
  }
  return normalizeOpcPartName(resolvedSegments.join('/'))
}

const checkEntrySizes = (
  compressedSize: number | undefined,
  uncompressedSize: number | undefined,
) => {
  if (
    !Number.isFinite(compressedSize) ||
    !Number.isFinite(uncompressedSize) ||
    compressedSize == null ||
    uncompressedSize == null ||
    compressedSize < 0 ||
    uncompressedSize < 0 ||
    (uncompressedSize > 0 && compressedSize === 0)
  ) {
    throw new Error('The Office archive does not expose safe part sizes.')
  }
  return { compressedSize, uncompressedSize }
}

const getEntrySizes = (entry: JSZipObject) => {
  const data = (entry as InspectableZipObject)._data
  return checkEntrySizes(data?.compressedSize, data?.uncompressedSize)
}

/**
 * Backend-neutral handle for one archive entry. The buffer backend wraps
 * JSZip's eagerly loaded objects; the Blob backend reads entries on demand so
 * the archive's payload never has to be retained in the JS heap.
 */
interface ArchiveEntryHandle {
  isDirectory: boolean
  name: string
  originalName: string
  readBytes: () => Promise<Uint8Array>
  readString: () => Promise<string>
  sizes: () => { compressedSize: number; uncompressedSize: number }
}

const openBufferBackend = async (
  buffer: ArrayBuffer,
): Promise<ArchiveEntryHandle[]> => {
  const zip = await JSZip.loadAsync(buffer, {
    checkCRC32: false,
    createFolders: false,
  })
  return Object.values(zip.files).map((entry) => ({
    isDirectory: entry.dir,
    name: entry.name,
    originalName: entry.unsafeOriginalName ?? entry.name,
    readBytes: () => entry.async('uint8array'),
    readString: () => entry.async('string'),
    sizes: () => getEntrySizes(entry),
  }))
}

const openBlobBackend = async (blob: Blob): Promise<ArchiveEntryHandle[]> => {
  const archive = await openBlobArchive(blob)
  return archive.entries.map((entry) => ({
    isDirectory: entry.isDirectory,
    name: entry.name,
    originalName: entry.name,
    readBytes: () => archive.readBytes(entry.name),
    readString: () => archive.readString(entry.name),
    sizes: () => checkEntrySizes(entry.compressedSize, entry.uncompressedSize),
  }))
}

const validateArchive = (
  sourceBytes: number,
  entries: ArchiveEntryHandle[],
) => {
  if (sourceBytes > MAX_SOURCE_BYTES) {
    throw new Error('The Office document exceeds the supported source size.')
  }
  if (entries.length > MAX_ARCHIVE_ENTRIES) {
    throw new Error('The Office archive contains too many parts.')
  }

  let expandedBytes = 0
  for (const entry of entries) {
    normalizeOpcPartName(entry.name)
    normalizeOpcPartName(entry.originalName)
    if (entry.isDirectory) {
      continue
    }

    const { compressedSize, uncompressedSize } = entry.sizes()
    if (uncompressedSize > MAX_ARCHIVE_ENTRY_BYTES) {
      throw new Error('The Office document contains an oversized package part.')
    }
    if (
      compressedSize > 0 &&
      uncompressedSize / compressedSize > MAX_COMPRESSION_RATIO
    ) {
      throw new Error(
        'The Office document contains an unsafe compression ratio.',
      )
    }
    expandedBytes += uncompressedSize
    if (expandedBytes > MAX_ARCHIVE_EXPANDED_BYTES) {
      throw new Error('The Office document expands beyond the supported limit.')
    }
  }
}

const parseContentTypes = (xml: string): ContentTypes => {
  const document = parseOoxmlXml(xml)
  const defaults = new Map<string, string>()
  const overrides = new Map<string, string>()
  for (const element of descendantsByName(document, 'Default')) {
    const extension = element.getAttribute('Extension')?.toLowerCase()
    const contentType = element.getAttribute('ContentType')
    if (extension && contentType) {
      defaults.set(extension, contentType)
    }
  }
  for (const element of descendantsByName(document, 'Override')) {
    const partName = element.getAttribute('PartName')
    const contentType = element.getAttribute('ContentType')
    if (partName && contentType) {
      overrides.set(normalizeOpcPartName(partName), contentType)
    }
  }
  return { defaults, overrides }
}

const contentTypeForPath = (contentTypes: ContentTypes, path: string) => {
  const override = contentTypes.overrides.get(path)
  if (override) {
    return override
  }
  const extension = path.includes('.')
    ? path.slice(path.lastIndexOf('.') + 1).toLowerCase()
    : ''
  return contentTypes.defaults.get(extension) ?? GENERIC_BINARY_CONTENT_TYPE
}

const extensionForPath = (path: string) =>
  path.includes('.') ? path.slice(path.lastIndexOf('.') + 1).toLowerCase() : ''

const isGenericBinaryContentType = (contentType: string) =>
  contentType.split(';', 1)[0]?.trim().toLowerCase() ===
  GENERIC_BINARY_CONTENT_TYPE

const trustedImageFormatForPath = (path: string) =>
  TRUSTED_IMAGE_FORMAT_BY_EXTENSION.get(extensionForPath(path))

const normalizedMediaContentType = (
  contentTypes: ContentTypes,
  path: string,
  bytes: Uint8Array,
) => {
  const declaredContentType = contentTypeForPath(contentTypes, path)
  if (!isGenericBinaryContentType(declaredContentType)) {
    return declaredContentType
  }
  const trustedImageFormat = trustedImageFormatForPath(path)
  return trustedImageFormat?.matchesSignature(bytes)
    ? trustedImageFormat.contentType
    : declaredContentType
}

const isPotentialMediaPath = (contentTypes: ContentTypes, path: string) => {
  const declaredContentType = contentTypeForPath(contentTypes, path)
  return (
    /^(?:audio|image|video)\//.test(declaredContentType) ||
    (isGenericBinaryContentType(declaredContentType) &&
      trustedImageFormatForPath(path) != null)
  )
}

export interface OoxmlPackage {
  entries: readonly string[]
  externalRelationshipCount: () => number
  has: (path: string) => boolean
  media: OoxmlMediaResolver
  officeDocumentPath: string
  readBytes: (path: string, signal?: AbortSignal) => Promise<Uint8Array>
  readXml: (path: string, signal?: AbortSignal) => Promise<string>
  relationshipsFor: (
    sourcePart: string,
    signal?: AbortSignal,
  ) => Promise<OoxmlRelationship[]>
  releaseExpandedText: () => void
  uncompressedSize: (path: string) => number
}

export const openOoxmlPackage = async (
  source: ArrayBuffer | Blob,
  signal?: AbortSignal,
): Promise<OoxmlPackage> => {
  throwIfAborted(signal)
  const sourceBytes = source instanceof Blob ? source.size : source.byteLength
  if (sourceBytes > MAX_SOURCE_BYTES) {
    throw new Error('The Office document exceeds the supported source size.')
  }
  const rawEntries =
    source instanceof Blob
      ? await openBlobBackend(source)
      : await openBufferBackend(source)
  throwIfAborted(signal)

  validateArchive(sourceBytes, rawEntries)
  const entriesByPath = new Map<string, ArchiveEntryHandle>()
  const canonicalPathByEntry = new Map<ArchiveEntryHandle, string>()
  const registerPath = (path: string, entry: ArchiveEntryHandle) => {
    const existing = entriesByPath.get(path)
    if (existing && existing !== entry) {
      throw new Error('The Office archive contains colliding package paths.')
    }
    entriesByPath.set(path, entry)
  }
  for (const entry of rawEntries) {
    if (entry.isDirectory) {
      continue
    }
    const rawPath = normalizeOpcPartName(entry.name)
    const decodedPath = normalizeOpcPartName(entry.originalName)
    registerPath(rawPath, entry)
    registerPath(decodedPath, entry)
    canonicalPathByEntry.set(entry, decodedPath)
  }

  const entryForPath = (path: string) => {
    const normalizedPath = normalizeOpcPartName(path)
    const entry = entriesByPath.get(normalizedPath)
    if (!entry) {
      throw new Error(
        `The Office document is missing the package part ${normalizedPath}.`,
      )
    }
    return entry
  }

  const contentTypesEntry = entriesByPath.get('[Content_Types].xml')
  if (!contentTypesEntry) {
    throw new Error('The Office document is missing [Content_Types].xml.')
  }
  const rawContentTypes = await contentTypesEntry.readString()
  if (rawContentTypes.length > MAX_XML_CHARACTERS_PER_PART) {
    throw new Error('The Office document contains an oversized XML part.')
  }
  const contentTypes = parseContentTypes(rawContentTypes)
  throwIfAborted(signal)

  const xmlPromises = new Map<string, Promise<string>>()
  const relationshipPromises = new Map<string, Promise<OoxmlRelationship[]>>()
  let externalRelationships = 0
  let expandedXmlCharacters = rawContentTypes.length

  const readXml = async (path: string, readSignal?: AbortSignal) => {
    throwIfAborted(readSignal)
    const normalizedPath = normalizeOpcPartName(path)
    let promise = xmlPromises.get(normalizedPath)
    if (!promise) {
      promise = entryForPath(normalizedPath)
        .readString()
        .then((xml) => {
          if (xml.length > MAX_XML_CHARACTERS_PER_PART) {
            throw new Error(
              'The Office document contains an oversized XML part.',
            )
          }
          expandedXmlCharacters += xml.length
          if (expandedXmlCharacters > MAX_XML_CHARACTERS_TOTAL) {
            throw new Error(
              'The Office document contains too much expanded XML.',
            )
          }
          return xml
        })
      xmlPromises.set(normalizedPath, promise)
    }
    const xml = await promise
    throwIfAborted(readSignal)
    return xml
  }

  const readBytes = async (path: string, readSignal?: AbortSignal) => {
    throwIfAborted(readSignal)
    const bytes = await entryForPath(path).readBytes()
    throwIfAborted(readSignal)
    return bytes
  }

  const relationshipsFor = async (
    sourcePart: string,
    readSignal?: AbortSignal,
  ) => {
    const relationshipPath = getOpcRelationshipPartName(sourcePart)
    if (!entriesByPath.has(relationshipPath)) {
      return []
    }

    let promise = relationshipPromises.get(relationshipPath)
    if (!promise) {
      promise = readXml(relationshipPath, readSignal).then((xml) => {
        const document = parseOoxmlXml(xml)
        const relationships: OoxmlRelationship[] = []
        for (const element of descendantsByName(document, 'Relationship')) {
          const id = attributeByLocalName(element, 'Id')
          const target = attributeByLocalName(element, 'Target')
          const type = attributeByLocalName(element, 'Type')
          if (!id || !target || !type) {
            continue
          }
          const external =
            attributeByLocalName(element, 'TargetMode')
              ?.trim()
              .toLowerCase() === 'external'
          if (external) {
            externalRelationships += 1
          }
          relationships.push({
            external,
            id,
            target: external
              ? target
              : resolveOpcRelationshipTarget(sourcePart, target),
            type,
          })
        }
        return relationships
      })
      relationshipPromises.set(relationshipPath, promise)
    }
    const relationships = await promise
    throwIfAborted(readSignal)
    return relationships
  }

  // Generic content types are common in third-party Office packages. Include
  // only browser-supported image extensions in the lazy media budget here;
  // the bytes are signature-verified when the part is actually read.
  const mediaEntries = [...canonicalPathByEntry.entries()].filter(([, path]) =>
    isPotentialMediaPath(contentTypes, path),
  )
  const loadedMedia = new Map<ArchiveEntryHandle, number>()
  // Stage and thumbnail rendering can request the same media together. Share
  // only the active expansion; settled Blobs are deliberately not retained.
  const inFlightMedia = new Map<ArchiveEntryHandle, Promise<Blob>>()
  const totalMediaBytes = mediaEntries.reduce(
    (total, [entry]) => total + entry.sizes().uncompressedSize,
    0,
  )
  const rootRelationships = await relationshipsFor('', signal)
  const officeDocumentRelationship = relationshipByOfficeType(rootRelationships)
  if (!officeDocumentRelationship || officeDocumentRelationship.external) {
    throw new Error(
      'The Office package does not identify its main document part.',
    )
  }

  return {
    entries: [...canonicalPathByEntry.values()],
    externalRelationshipCount: () => externalRelationships,
    has: (path) => entriesByPath.has(normalizeOpcPartName(path)),
    media: {
      getMetrics: () => ({
        loadedBytes: [...loadedMedia.values()].reduce(
          (total, size) => total + size,
          0,
        ),
        loadedCount: loadedMedia.size,
        totalBytes: totalMediaBytes,
        totalCount: mediaEntries.length,
      }),
      read: (path) => {
        const normalizedPath = normalizeOpcPartName(path)
        const entry = entryForPath(normalizedPath)
        const existingPromise = inFlightMedia.get(entry)
        if (existingPromise) {
          return existingPromise
        }
        const promise = readBytes(normalizedPath).then((bytes) => {
          // JSZip returns an ArrayBuffer-backed view in browsers. Re-wrap that
          // buffer without copying so TypeScript can satisfy BlobPart's
          // narrower ArrayBuffer type; retain a defensive copy only for an
          // unexpected SharedArrayBuffer-backed implementation.
          const blobBytes: Uint8Array<ArrayBuffer> =
            bytes.buffer instanceof ArrayBuffer
              ? new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength)
              : Uint8Array.from(bytes)
          const blob = new Blob([blobBytes], {
            type: normalizedMediaContentType(
              contentTypes,
              normalizedPath,
              bytes,
            ),
          })
          loadedMedia.set(entry, blob.size)
          return blob
        })
        inFlightMedia.set(entry, promise)
        const clearInFlight = () => {
          if (inFlightMedia.get(entry) === promise) {
            inFlightMedia.delete(entry)
          }
        }
        void promise.then(clearInFlight, clearInFlight)
        return promise
      },
    },
    officeDocumentPath: officeDocumentRelationship.target,
    readBytes,
    readXml,
    relationshipsFor,
    releaseExpandedText: () => {
      xmlPromises.clear()
      relationshipPromises.clear()
      expandedXmlCharacters = rawContentTypes.length
    },
    uncompressedSize: (path) => entryForPath(path).sizes().uncompressedSize,
  }
}

const relationshipByOfficeType = (
  relationships: readonly OoxmlRelationship[],
) =>
  relationships.find((relationship) =>
    relationship.type.endsWith('/officeDocument'),
  )
